import React, { Component } from 'react'
import { navigate } from '@reach/router'
import { css, StyleSheet } from 'aphrodite'
import Accordion from 'react-bootstrap/Accordion'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import BootstrapForm from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import ReactJson from 'react-json-view'

import AppStore from 'AppStore'
import { Header, Page } from 'components'
import { ReferralCampaignApi } from 'api'
import {
  pages,
  ReferralBaseProduct,
  ReferralCampaign,
  ReferralCampaignCode,
  ReferralCampaignProduct,
} from 'types'
import { isSuperAdmin } from 'utils/permission'
import { referralCampaigns } from 'utils/endpoints'

interface Props {
  path: string
  campaignKey?: string
}

type ValidationErrors = Record<string, string>

type JsonModalState = {
  codeIndex?: number
  draft: string
  error?: string
  target: 'campaign' | 'code'
  visible: boolean
  warningAccepted: boolean
}

interface State {
  activeCodeKey: string
  activeProductKey: string
  activeSectionKey: string
  baseProducts: ReferralBaseProduct[]
  campaign: ReferralCampaign
  campaignMetadataJson: string
  codeMetadataJsons: string[]
  dirty: boolean
  jsonModal: JsonModalState
  loading: boolean
  saving: boolean
  validationErrors: ValidationErrors
}

const DEFAULT_DISCOUNT_SKU_SUFFIX = '_discount10'

const emptyProduct = (): ReferralCampaignProduct => ({
  baseProductRef: '',
})

const emptyCode = (): ReferralCampaignCode => ({
  code: '',
  enabled: true,
})

const emptyJsonModal = (): JsonModalState => ({
  draft: '{}',
  target: 'campaign',
  visible: false,
  warningAccepted: false,
})

export default class CreateReferralCampaignPage extends Component<Props, State> {
  fieldAnchors: Record<string, HTMLDivElement | null> = {}
  fieldInputs: Record<string, any> = {}
  page?: Page

  state: State = {
    activeCodeKey: 'code-0',
    activeProductKey: 'product-0',
    activeSectionKey: 'campaign',
    baseProducts: [],
    campaign: {
      products: [emptyProduct()],
      codes: [emptyCode()],
      metadata: {},
    },
    campaignMetadataJson: '{}',
    codeMetadataJsons: ['{}'],
    dirty: false,
    jsonModal: emptyJsonModal(),
    loading: true,
    saving: false,
    validationErrors: {},
  }

  async componentDidMount() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    try {
      const baseProductsPromise = ReferralCampaignApi.getBaseProducts()
      const campaignPromise = this.props.campaignKey
        ? ReferralCampaignApi.getCampaign(this.props.campaignKey)
        : Promise.resolve(undefined)
      const [baseProducts, campaign] = await Promise.all([baseProductsPromise, campaignPromise])

      if (campaign) {
        const normalizedCampaign = this.normalizeCampaignForForm(campaign)
        this.setState({
          activeCodeKey: 'code-0',
          activeProductKey: 'product-0',
          activeSectionKey: 'campaign',
          baseProducts,
          campaign: normalizedCampaign,
          campaignMetadataJson: JSON.stringify(campaign.metadata ?? {}, null, 2),
          codeMetadataJsons: normalizedCampaign.codes.map((code) => JSON.stringify(code.metadata ?? {}, null, 2)),
          dirty: false,
          loading: false,
        })
        return
      }

      this.setState({ baseProducts, dirty: false, loading: false })
    } catch (e) {
      this.setState({ loading: false })
      this.page?.showNotification({ message: this.getErrorMessage(e), type: 'error' })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.state.dirty) return

    event.preventDefault()
    event.returnValue = ''
    return ''
  }

  normalizeCampaignForForm = (campaign: ReferralCampaign): ReferralCampaign => ({
    ...campaign,
    campaignStartsAt: this.formatDateForInput(campaign.campaignStartsAt),
    campaignEndsAt: this.formatDateForInput(campaign.campaignEndsAt),
    products: campaign.products && campaign.products.length > 0 ? campaign.products : [emptyProduct()],
    codes: campaign.codes && campaign.codes.length > 0 ? campaign.codes : [emptyCode()],
  })

  getErrorMessage = (error: any) => {
    return error?.response?.data?.errorDescription ?? error?.response?.data?.message ?? error?.message ?? 'Operazione non riuscita.'
  }

  parseNumber = (value: string) => {
    const trimmedValue = value.trim()
    if (trimmedValue === '') return undefined
    return Number(trimmedValue)
  }

  cleanValue = (value: any) => {
    if (value === null) return undefined
    if (typeof value !== 'string') return value
    const trimmedValue = value.trim()
    return trimmedValue ? trimmedValue : undefined
  }

  compactObject = (value: { [key: string]: any }) => {
    return Object.keys(value).reduce((result: { [key: string]: any }, key) => {
      const entryValue = this.cleanValue(value[key])
      if (entryValue !== undefined) result[key] = entryValue
      return result
    }, {})
  }

  clearValidationError = (errors: ValidationErrors, fieldKey: string) => {
    if (!errors[fieldKey]) return errors

    const nextErrors = { ...errors }
    delete nextErrors[fieldKey]
    return nextErrors
  }

  setFieldAnchor = (fieldKey: string) => (element: HTMLDivElement | null) => {
    this.fieldAnchors[fieldKey] = element
  }

  setFieldInput = (fieldKey: string) => (element: any) => {
    this.fieldInputs[fieldKey] = element
  }

  setCampaignField = (key: keyof ReferralCampaign, value: any) => {
    const fieldKey = `campaign.${String(key)}`
    this.setState((state) => ({
      campaign: { ...state.campaign, [key]: value },
      dirty: true,
      validationErrors: this.clearValidationError(state.validationErrors, fieldKey),
    }))
  }

  setCampaignNumberField = (key: keyof ReferralCampaign, value: string) => {
    this.setCampaignField(key, this.parseNumber(value))
  }

  setProductField = (index: number, key: keyof ReferralCampaignProduct, value: any) => {
    const fieldKey = `product.${index}.${String(key)}`
    this.setState((state) => {
      const products = [...state.campaign.products]
      products[index] = { ...products[index], [key]: value }

      return {
        campaign: { ...state.campaign, products },
        dirty: true,
        validationErrors: this.clearValidationError(state.validationErrors, fieldKey),
      }
    })
  }

  setCodeField = (index: number, key: keyof ReferralCampaignCode, value: any) => {
    const fieldKey = `code.${index}.${String(key)}`
    this.setState((state) => {
      const codes = [...state.campaign.codes]
      codes[index] = { ...codes[index], [key]: value }

      return {
        campaign: { ...state.campaign, codes },
        dirty: true,
        validationErrors: this.clearValidationError(state.validationErrors, fieldKey),
      }
    })
  }

  setCodeNumberField = (index: number, key: keyof ReferralCampaignCode, value: string) => {
    this.setCodeField(index, key, this.parseNumber(value))
  }

  getReferralLinkEnvironmentLabel = () => (AppStore.environment === 'prod' ? 'PROD' : 'STAGING')

  getReferralLinkHost = () =>
    AppStore.environment === 'prod' ? 'app.specialquiz.it' : 'staging-app.specialquiz.it'

  getReferralLink = (code?: string) => {
    const normalizedCode = code?.trim().toUpperCase()
    if (!normalizedCode) return undefined

    return `https://${this.getReferralLinkHost()}/referral/${normalizedCode}`
  }

  copyToClipboard = async (value: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return
    }

    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.setAttribute('readonly', 'true')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  copyReferralLink = async (code?: string) => {
    const referralLink = this.getReferralLink(code)
    if (!referralLink) {
      this.page?.showNotification({ message: 'Inserisci un codice prima di copiare il link.', type: 'warning' })
      return
    }

    try {
      await this.copyToClipboard(referralLink)
      this.page?.showNotification({
        message: `Link ${this.getReferralLinkEnvironmentLabel()} copiato: ${referralLink}`,
        type: 'success',
      })
    } catch (e) {
      this.page?.showNotification({ message: 'Non e stato possibile copiare il link.', type: 'error' })
    }
  }

  addProduct = () => {
    const nextIndex = this.state.campaign.products.length
    this.setState((state) => ({
      activeProductKey: `product-${nextIndex}`,
      activeSectionKey: 'products',
      campaign: { ...state.campaign, products: [...state.campaign.products, emptyProduct()] },
      dirty: true,
    }))
  }

  removeProduct = (index: number) => {
    this.page?.showConfirmDialog(
      'Rimuovere prodotto?',
      'Il prodotto verra rimosso dalla campagna in modifica. La modifica sara effettiva solo dopo il salvataggio.',
      () => {
        this.setState((state) => {
          const products = state.campaign.products.filter((_, i) => i !== index)
          const nextProducts = products.length > 0 ? products : [emptyProduct()]
          const nextIndex = Math.min(index, nextProducts.length - 1)

          return {
            activeProductKey: `product-${nextIndex}`,
            activeSectionKey: 'products',
            campaign: { ...state.campaign, products: nextProducts },
            dirty: true,
          }
        })
      }
    )
  }

  addCode = () => {
    const nextIndex = this.state.campaign.codes.length
    this.setState((state) => ({
      activeCodeKey: `code-${nextIndex}`,
      activeSectionKey: 'codes',
      campaign: { ...state.campaign, codes: [...state.campaign.codes, emptyCode()] },
      codeMetadataJsons: [...state.codeMetadataJsons, '{}'],
      dirty: true,
    }))
  }

  removeCode = (index: number) => {
    this.page?.showConfirmDialog(
      'Rimuovere codice?',
      'Il codice verra rimosso dal payload della campagna. Se esiste gia su DB, il backend lo disabilitera senza hard delete.',
      () => {
        this.setState((state) => {
          const codes = state.campaign.codes.filter((_, i) => i !== index)
          const codeMetadataJsons = state.codeMetadataJsons.filter((_, i) => i !== index)
          const nextCodes = codes.length > 0 ? codes : [emptyCode()]
          const nextMetadata = codeMetadataJsons.length > 0 ? codeMetadataJsons : ['{}']
          const nextIndex = Math.min(index, nextCodes.length - 1)

          return {
            activeCodeKey: `code-${nextIndex}`,
            activeSectionKey: 'codes',
            campaign: { ...state.campaign, codes: nextCodes },
            codeMetadataJsons: nextMetadata,
            dirty: true,
          }
        })
      }
    )
  }

  parseJson = (value: string, label: string) => {
    try {
      return value.trim() ? JSON.parse(value) : {}
    } catch (e) {
      throw new Error(`${label} non e un JSON valido.`)
    }
  }

  formatDateForInput = (value?: string) => {
    if (!value) return undefined

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return undefined

    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  localDateToIso = (value: string | undefined, mode: 'start' | 'end') => {
    if (!value) return undefined

    const [year, month, day] = value.split('-').map((part) => Number(part))
    if (!year || !month || !day) return undefined

    const date =
      mode === 'start'
        ? new Date(year, month - 1, day, 0, 0, 0, 0)
        : new Date(year, month - 1, day, 23, 59, 59, 999)
    return date.toISOString()
  }

  appendSuffix = (value: string | undefined, suffix: string, casing: 'lower' | 'preserve' | 'upper') => {
    if (!value) return '-'

    const normalizedSuffix =
      casing === 'upper' ? suffix.toUpperCase() : casing === 'lower' ? suffix.toLowerCase() : suffix
    return `${value.trim()}${normalizedSuffix}`
  }

  getDiscountSkuSuffix = () => this.state.campaign.discountSkuSuffix?.trim() || DEFAULT_DISCOUNT_SKU_SUFFIX

  getBaseProductValue = (product: ReferralBaseProduct) => {
    return product.ref ?? product._id ?? product.id ?? product.storeProductId ?? ''
  }

  getBaseProductLabel = (product: ReferralBaseProduct, isHiddenCurrent?: boolean) => {
    const value = this.getBaseProductValue(product)
    const label = `${product.title ?? product.name ?? product.id ?? value}${
      product.storeProductId ? ` (${product.storeProductId})` : ''
    }`

    return isHiddenCurrent ? `${label} - attuale/non visibile` : label
  }

  getBaseProductOptionsForProduct = (product: ReferralCampaignProduct) => {
    const selectedValue = product.baseProductRef
    const options = [...this.state.baseProducts]
    const selectedExists = options.some((baseProduct) => this.getBaseProductValue(baseProduct) === selectedValue)

    if (!selectedExists && product.baseProduct) {
      options.push({ ...product.baseProduct, ref: selectedValue })
    }

    return options
  }

  getSelectedBaseProduct = (product: ReferralCampaignProduct) => {
    const selectedValue = product.baseProductRef
    if (!selectedValue) return undefined

    return (
      this.getBaseProductOptionsForProduct(product).find(
        (baseProduct) => this.getBaseProductValue(baseProduct) === selectedValue
      ) ?? product.baseProduct
    )
  }

  getDerivedPromoProduct = (product: ReferralCampaignProduct) => {
    const baseProduct = this.getSelectedBaseProduct(product)
    if (!baseProduct) return undefined

    const suffix = this.getDiscountSkuSuffix()
    const keySource = baseProduct.key ?? baseProduct.id

    return {
      id: this.appendSuffix(baseProduct.id, suffix, 'upper'),
      key: this.appendSuffix(keySource, suffix, 'lower'),
      storeProductId: this.appendSuffix(baseProduct.storeProductId, suffix, 'preserve'),
      title: `${baseProduct.title ?? baseProduct.name ?? baseProduct.id ?? 'Prodotto premium'} - ${
        this.state.campaign.campaignKey ?? 'campaign'
      }`,
    }
  }

  getFieldError = (fieldKey: string) => this.state.validationErrors[fieldKey]

  hasErrorPrefix = (prefix: string) => {
    return Object.keys(this.state.validationErrors).some((key) => key.startsWith(prefix))
  }

  validateRequired = (errors: ValidationErrors, fieldKey: string, value: any, label: string) => {
    if (value === undefined || value === null || value === '') {
      errors[fieldKey] = `${label} e obbligatorio.`
    }
  }

  validateNumber = (errors: ValidationErrors, fieldKey: string, value: any, label: string) => {
    if (value === undefined || value === null || value === '') return
    if (typeof value !== 'number' || Number.isNaN(value)) {
      errors[fieldKey] = `${label} deve essere un numero.`
    }
  }

  validatePositiveNumber = (errors: ValidationErrors, fieldKey: string, value: any, label: string) => {
    this.validateNumber(errors, fieldKey, value, label)
    if (!errors[fieldKey] && typeof value === 'number' && value <= 0) {
      errors[fieldKey] = `${label} deve essere maggiore di zero.`
    }
  }

  validateAndBuildPayload = () => {
    const { campaign, campaignMetadataJson, codeMetadataJsons } = this.state
    const errors: ValidationErrors = {}

    this.validateRequired(errors, 'campaign.campaignKey', campaign.campaignKey, 'Campaign key')
    this.validateRequired(errors, 'campaign.campaignName', campaign.campaignName, 'Nome campagna')
    this.validateRequired(errors, 'campaign.discountPercent', campaign.discountPercent, 'Sconto %')
    this.validatePositiveNumber(errors, 'campaign.discountPercent', campaign.discountPercent, 'Sconto %')
    this.validateNumber(errors, 'campaign.durationDays', campaign.durationDays, 'Giorni durata')
    this.validateNumber(errors, 'campaign.activeHours', campaign.activeHours, 'Ore attive')
    this.validateNumber(errors, 'campaign.commissionPercent', campaign.commissionPercent, 'Commissione %')

    if (campaign.campaignStartsAt && campaign.campaignEndsAt && campaign.campaignEndsAt < campaign.campaignStartsAt) {
      errors['campaign.campaignEndsAt'] = 'Fine campagna deve essere successiva o uguale all inizio campagna.'
    }

    const selectedProducts = new Set<string>()
    const duplicateProducts = new Set<string>()
    const products = campaign.products.map((product, index) => {
      const baseProductRef = this.cleanValue(product.baseProductRef)
      const fieldKey = `product.${index}.baseProductRef`

      this.validateRequired(errors, fieldKey, baseProductRef, `Prodotto ${index + 1}: prodotto base`)
      if (baseProductRef) {
        if (selectedProducts.has(baseProductRef)) duplicateProducts.add(baseProductRef)
        selectedProducts.add(baseProductRef)
      }

      return { baseProductRef }
    })

    if (duplicateProducts.size > 0) {
      campaign.products.forEach((product, index) => {
        const baseProductRef = this.cleanValue(product.baseProductRef)
        if (baseProductRef && duplicateProducts.has(baseProductRef)) {
          errors[`product.${index}.baseProductRef`] = 'Prodotto base gia selezionato nella campagna.'
        }
      })
    }

    const selectedCodes = new Set<string>()
    const duplicateCodes = new Set<string>()
    const codes = campaign.codes.map((code, index) => {
      const codeValue = this.cleanValue(code.code)
      const codeFieldKey = `code.${index}.code`
      const maxRedemptionsFieldKey = `code.${index}.maxRedemptions`
      const commissionFieldKey = `code.${index}.commissionPercent`

      this.validateRequired(errors, codeFieldKey, codeValue, `Codice ${index + 1}`)
      this.validateNumber(errors, maxRedemptionsFieldKey, code.maxRedemptions, `Codice ${index + 1}: max redemptions`)
      this.validateNumber(errors, commissionFieldKey, code.commissionPercent, `Codice ${index + 1}: commissione`)

      if (!errors[maxRedemptionsFieldKey] && typeof code.maxRedemptions === 'number' && code.maxRedemptions <= 0) {
        errors[maxRedemptionsFieldKey] = `Codice ${index + 1}: max redemptions deve essere maggiore di zero.`
      }

      const normalizedCode = typeof codeValue === 'string' ? codeValue.trim().toUpperCase() : ''
      if (normalizedCode) {
        if (selectedCodes.has(normalizedCode)) duplicateCodes.add(normalizedCode)
        selectedCodes.add(normalizedCode)
      }

      return this.compactObject({
        code: codeValue,
        commissionPercent: code.commissionPercent,
        creatorEmail: code.creatorEmail,
        creatorName: code.creatorName,
        creatorSlug: code.creatorSlug,
        enabled: code.enabled !== false,
        label: code.label,
        maxRedemptions: code.maxRedemptions,
        metadata: this.parseJson(codeMetadataJsons[index] ?? '{}', `Codice ${index + 1}: metadata`),
      })
    })

    if (duplicateCodes.size > 0) {
      campaign.codes.forEach((code, index) => {
        const codeValue = this.cleanValue(code.code)
        const normalizedCode = typeof codeValue === 'string' ? codeValue.trim().toUpperCase() : ''
        if (normalizedCode && duplicateCodes.has(normalizedCode)) {
          errors[`code.${index}.code`] = 'Codice duplicato nella campagna.'
        }
      })
    }

    const payload = this.compactObject({
      activeHours: campaign.activeHours,
      campaignEndsAt: this.localDateToIso(campaign.campaignEndsAt, 'end'),
      campaignKey: campaign.campaignKey,
      campaignName: campaign.campaignName,
      campaignStartsAt: this.localDateToIso(campaign.campaignStartsAt, 'start'),
      codes,
      commissionPercent: campaign.commissionPercent,
      description: campaign.description,
      discountPercent: campaign.discountPercent,
      discountSkuSuffix: campaign.discountSkuSuffix,
      durationDays: campaign.durationDays,
      metadata: this.parseJson(campaignMetadataJson, 'Metadata campagna'),
      products,
    }) as ReferralCampaign

    return { errors, payload }
  }

  getErrorTargetState = (fieldKey: string) => {
    if (fieldKey.startsWith('product.')) {
      const index = fieldKey.split('.')[1]
      return {
        activeCodeKey: this.state.activeCodeKey,
        activeProductKey: `product-${index}`,
        activeSectionKey: 'products',
      }
    }
    if (fieldKey.startsWith('code.')) {
      const index = fieldKey.split('.')[1]
      return {
        activeCodeKey: `code-${index}`,
        activeProductKey: this.state.activeProductKey,
        activeSectionKey: 'codes',
      }
    }
    return {
      activeCodeKey: this.state.activeCodeKey,
      activeProductKey: this.state.activeProductKey,
      activeSectionKey: fieldKey.startsWith('metadata.') ? 'metadata' : 'campaign',
    }
  }

  scrollToField = (fieldKey: string) => {
    const anchor = this.fieldAnchors[fieldKey]
    const input = this.fieldInputs[fieldKey]

    if (anchor?.scrollIntoView) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    if (input?.focus) {
      window.setTimeout(() => input.focus(), 250)
    }
  }

  jumpToField = (fieldKey: string) => {
    this.setState(this.getErrorTargetState(fieldKey), () => this.scrollToField(fieldKey))
  }

  onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      const { errors, payload } = this.validateAndBuildPayload()

      if (Object.keys(errors).length > 0) {
        const firstErrorKey = Object.keys(errors)[0]
        this.setState(
          {
            ...this.getErrorTargetState(firstErrorKey),
            validationErrors: errors,
          },
          () => {
            this.scrollToField(firstErrorKey)
            this.page?.showNotification({ message: 'Correggi i campi evidenziati prima di salvare.', type: 'error' })
          }
        )
        return
      }

      this.setState({ saving: true, validationErrors: {} })

      if (this.props.campaignKey) {
        await ReferralCampaignApi.updateCampaign(this.props.campaignKey, payload)
        await AppStore.changeNotification({ message: 'Campagna referral aggiornata.', type: 'success' })
      } else {
        await ReferralCampaignApi.createCampaign(payload)
        await AppStore.changeNotification({ message: 'Campagna referral creata.', type: 'success' })
      }

      this.setState({ dirty: false })
      navigate(referralCampaigns)
    } catch (e) {
      this.setState({ saving: false })
      this.page?.showNotification({ message: this.getErrorMessage(e), type: 'error' })
    }
  }

  openMetadataModal = (target: 'campaign' | 'code', codeIndex?: number) => {
    const draft =
      target === 'campaign' ? this.state.campaignMetadataJson : this.state.codeMetadataJsons[codeIndex ?? 0] ?? '{}'

    this.setState({
      jsonModal: {
        codeIndex,
        draft,
        target,
        visible: true,
        warningAccepted: false,
      },
    })
  }

  closeMetadataModal = () => {
    this.setState({ jsonModal: emptyJsonModal() })
  }

  acceptMetadataWarning = () => {
    this.setState((state) => ({
      jsonModal: { ...state.jsonModal, error: undefined, warningAccepted: true },
    }))
  }

  saveMetadataModal = () => {
    const { jsonModal } = this.state

    try {
      const parsedJson = this.parseJson(jsonModal.draft, 'Metadata')
      const formattedJson = JSON.stringify(parsedJson, null, 2)

      if (jsonModal.target === 'campaign') {
        this.setState({
          campaignMetadataJson: formattedJson,
          dirty: true,
          jsonModal: emptyJsonModal(),
        })
        return
      }

      this.setState((state) => {
        const codeMetadataJsons = [...state.codeMetadataJsons]
        codeMetadataJsons[jsonModal.codeIndex ?? 0] = formattedJson

        return {
          codeMetadataJsons,
          dirty: true,
          jsonModal: emptyJsonModal(),
        }
      })
    } catch (error) {
      this.setState((state) => ({
        jsonModal: { ...state.jsonModal, error: (error as any).message ?? 'JSON non valido.' },
      }))
    }
  }

  renderRequiredLabel = (label: string, required?: boolean) => (
    <>
      {label}
      {required && <span className={css(s.requiredMark)}> *</span>}
    </>
  )

  renderAccordionTitle = (content: React.ReactNode, isOpen: boolean) => (
    <span className={css(s.accordionTitle)}>
      <span className={css(s.accordionArrow, isOpen && s.accordionArrowOpen)}>{'>'}</span>
      <span className={css(s.accordionTitleText)}>{content}</span>
    </span>
  )

  renderTextControl = (
    fieldKey: string,
    label: string,
    value: any,
    onChange: (value: string) => void,
    type: string = 'text',
    as?: 'textarea',
    required?: boolean
  ) => {
    const error = this.getFieldError(fieldKey)

    return (
      <BootstrapForm.Group className={css(s.field)} ref={this.setFieldAnchor(fieldKey)}>
        <BootstrapForm.Label>{this.renderRequiredLabel(label, required)}</BootstrapForm.Label>
        <BootstrapForm.Control
          as={as}
          isInvalid={Boolean(error)}
          ref={this.setFieldInput(fieldKey)}
          rows={as === 'textarea' ? 4 : undefined}
          type={type}
          value={value ?? ''}
          onChange={(event: any) => onChange(event.currentTarget.value)}
        />
        {error && <BootstrapForm.Control.Feedback type="invalid">{error}</BootstrapForm.Control.Feedback>}
      </BootstrapForm.Group>
    )
  }

  renderErrorSummary = () => {
    const errors = this.state.validationErrors
    const errorEntries = Object.entries(errors)
    if (errorEntries.length === 0) return null

    return (
      <Alert variant="danger" className={css(s.errorSummary)}>
        <b>Correggi questi campi prima di salvare:</b>
        <ul className={css(s.errorList)}>
          {errorEntries.map(([fieldKey, message]) => (
            <li key={fieldKey}>
              <Button variant="link" className={css(s.errorLink)} onClick={() => this.jumpToField(fieldKey)}>
                {message}
              </Button>
            </li>
          ))}
        </ul>
      </Alert>
    )
  }

  renderCampaignSection = () => {
    const { campaign } = this.state
    const hasErrors = this.hasErrorPrefix('campaign.')

    return (
      <Card className={css(hasErrors && s.cardWithErrors)}>
        <Card.Header className={css(s.cardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey="campaign" className={css(s.accordionToggle)}>
            {this.renderAccordionTitle(
              <>
                Campagna {hasErrors && <span className={css(s.errorBadge)}>Errori</span>}
              </>,
              this.state.activeSectionKey === 'campaign'
            )}
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey="campaign">
          <Card.Body>
            <div className={css(s.grid)}>
              {this.renderTextControl('campaign.campaignKey', 'Campaign key', campaign.campaignKey, (value) =>
                this.setCampaignField('campaignKey', value), 'text', undefined, true
              )}
              {this.renderTextControl('campaign.campaignName', 'Nome campagna', campaign.campaignName, (value) =>
                this.setCampaignField('campaignName', value), 'text', undefined, true
              )}
              {this.renderTextControl('campaign.campaignStartsAt', 'Inizio campagna', campaign.campaignStartsAt, (value) =>
                this.setCampaignField('campaignStartsAt', value), 'date'
              )}
              {this.renderTextControl('campaign.campaignEndsAt', 'Fine campagna', campaign.campaignEndsAt, (value) =>
                this.setCampaignField('campaignEndsAt', value), 'date'
              )}
              {this.renderTextControl('campaign.durationDays', 'Giorni durata', campaign.durationDays, (value) =>
                this.setCampaignNumberField('durationDays', value), 'number'
              )}
              {this.renderTextControl('campaign.activeHours', 'Ore attive', campaign.activeHours, (value) =>
                this.setCampaignNumberField('activeHours', value), 'number'
              )}
              {this.renderTextControl('campaign.discountPercent', 'Sconto %', campaign.discountPercent, (value) =>
                this.setCampaignNumberField('discountPercent', value), 'number', undefined, true
              )}
              {this.renderTextControl('campaign.commissionPercent', 'Commissione %', campaign.commissionPercent, (value) =>
                this.setCampaignNumberField('commissionPercent', value), 'number'
              )}
              {this.renderTextControl('campaign.discountSkuSuffix', 'SKU suffix sconto', campaign.discountSkuSuffix, (value) =>
                this.setCampaignField('discountSkuSuffix', value)
              )}
            </div>
            {this.renderTextControl('campaign.description', 'Descrizione', campaign.description, (value) =>
              this.setCampaignField('description', value), 'text', 'textarea'
            )}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderProductPreview = (product: ReferralCampaignProduct) => {
    const derivedPromoProduct = this.getDerivedPromoProduct(product)
    if (!derivedPromoProduct) {
      return <div className={css(s.previewMuted)}>Seleziona un prodotto base per vedere gli SKU promo derivati.</div>
    }

    return (
      <div className={css(s.previewGrid)}>
        <div className={css(s.previewItem)}>
          <b>Promo id</b>
          <span className={css(s.previewValue)}>{derivedPromoProduct.id}</span>
        </div>
        <div className={css(s.previewItem)}>
          <b>Promo key</b>
          <span className={css(s.previewValue)}>{derivedPromoProduct.key}</span>
        </div>
        <div className={css(s.previewItem)}>
          <b>Store product id</b>
          <span className={css(s.previewValue)}>{derivedPromoProduct.storeProductId}</span>
        </div>
        <div className={css(s.previewItem)}>
          <b>Titolo</b>
          <span className={css(s.previewValue)}>{derivedPromoProduct.title}</span>
        </div>
      </div>
    )
  }

  renderProduct = (product: ReferralCampaignProduct, index: number) => {
    const accordionKey = `product-${index}`
    const fieldKey = `product.${index}.baseProductRef`
    const error = this.getFieldError(fieldKey)
    const selectedBaseProduct = this.getSelectedBaseProduct(product)
    const derivedPromoProduct = this.getDerivedPromoProduct(product)
    const hasErrors = this.hasErrorPrefix(`product.${index}.`)
    const options = this.getBaseProductOptionsForProduct(product)
    const selectedValue = product.baseProductRef ?? ''
    const selectedExists = options.some((baseProduct) => this.getBaseProductValue(baseProduct) === selectedValue)

    return (
      <Card className={css(s.innerCard, hasErrors && s.cardWithErrors)} key={accordionKey}>
        <Card.Header className={css(s.innerCardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey={accordionKey} className={css(s.accordionToggle)}>
            {this.renderAccordionTitle(
              <>
                Prodotto {index + 1}:{' '}
                {selectedBaseProduct ? this.getBaseProductLabel(selectedBaseProduct) : 'non selezionato'}
                {derivedPromoProduct?.storeProductId && (
                  <span className={css(s.summaryMuted)}>
                    {' -> '}
                    {derivedPromoProduct.storeProductId}
                  </span>
                )}
              </>,
              this.state.activeProductKey === accordionKey
            )}
          </Accordion.Toggle>
          <Button variant="outline-danger" size="sm" onClick={() => this.removeProduct(index)}>
            Rimuovi
          </Button>
        </Card.Header>
        <Accordion.Collapse eventKey={accordionKey}>
          <Card.Body>
            <BootstrapForm.Group className={css(s.field)} ref={this.setFieldAnchor(fieldKey)}>
              <BootstrapForm.Label>{this.renderRequiredLabel('Prodotto base', true)}</BootstrapForm.Label>
              <BootstrapForm.Control
                as="select"
                isInvalid={Boolean(error)}
                ref={this.setFieldInput(fieldKey)}
                value={selectedValue}
                onChange={(event: any) => this.setProductField(index, 'baseProductRef', event.currentTarget.value)}
              >
                <option value="">Seleziona...</option>
                {!selectedExists && selectedValue && !product.baseProduct && (
                  <option value={selectedValue}>Attuale/non visibile ({selectedValue})</option>
                )}
                {options.map((baseProduct) => {
                  const value = this.getBaseProductValue(baseProduct)
                  return (
                    <option key={value} value={value}>
                      {this.getBaseProductLabel(baseProduct, baseProduct.ref === selectedValue && !selectedExists)}
                    </option>
                  )
                })}
              </BootstrapForm.Control>
              {error && <BootstrapForm.Control.Feedback type="invalid">{error}</BootstrapForm.Control.Feedback>}
            </BootstrapForm.Group>
            {this.renderProductPreview(product)}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderProductsSection = () => {
    const hasErrors = this.hasErrorPrefix('product.')

    return (
      <Card className={css(hasErrors && s.cardWithErrors)}>
        <Card.Header className={css(s.cardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey="products" className={css(s.accordionToggle)}>
            {this.renderAccordionTitle(
              <>
                Prodotti {hasErrors && <span className={css(s.errorBadge)}>Errori</span>}
              </>,
              this.state.activeSectionKey === 'products'
            )}
          </Accordion.Toggle>
          <Button variant="outline-primary" onClick={this.addProduct}>
            Aggiungi prodotto
          </Button>
        </Card.Header>
        <Accordion.Collapse eventKey="products">
          <Card.Body>
            <Accordion
              activeKey={this.state.activeProductKey}
              onSelect={(eventKey: any) => this.setState({ activeProductKey: eventKey ?? '' })}
            >
              {this.state.campaign.products.map(this.renderProduct)}
            </Accordion>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderCode = (code: ReferralCampaignCode, index: number) => {
    const accordionKey = `code-${index}`
    const hasErrors = this.hasErrorPrefix(`code.${index}.`)
    const codeLabel = code.code || `Codice ${index + 1}`
    const creatorLabel = code.creatorName ? ` - ${code.creatorName}` : ''
    const enabledLabel = code.enabled === false ? 'Disabilitato' : 'Abilitato'
    const referralLink = this.getReferralLink(code.code)

    return (
      <Card className={css(s.innerCard, hasErrors && s.cardWithErrors)} key={accordionKey}>
        <Card.Header className={css(s.innerCardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey={accordionKey} className={css(s.accordionToggle)}>
            {this.renderAccordionTitle(
              <>
                {codeLabel}
                {creatorLabel}
                <span className={css(s.summaryMuted)}> - {enabledLabel}</span>
              </>,
              this.state.activeCodeKey === accordionKey
            )}
          </Accordion.Toggle>
          <div className={css(s.headerActions)}>
            <Button
              disabled={!referralLink}
              title={referralLink ?? 'Inserisci un codice per generare il link referral'}
              variant="outline-secondary"
              size="sm"
              onClick={() => this.copyReferralLink(code.code)}
            >
              Copia link {this.getReferralLinkEnvironmentLabel()}
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => this.removeCode(index)}>
              Rimuovi
            </Button>
          </div>
        </Card.Header>
        <Accordion.Collapse eventKey={accordionKey}>
          <Card.Body>
            <div className={css(s.grid)}>
              {this.renderTextControl(`code.${index}.code`, 'Code', code.code, (value) =>
                this.setCodeField(index, 'code', value), 'text', undefined, true
              )}
              {this.renderTextControl(`code.${index}.label`, 'Label', code.label, (value) =>
                this.setCodeField(index, 'label', value)
              )}
              {this.renderTextControl(`code.${index}.maxRedemptions`, 'Max redemptions', code.maxRedemptions, (value) =>
                this.setCodeNumberField(index, 'maxRedemptions', value), 'number'
              )}
              {this.renderTextControl(`code.${index}.creatorName`, 'Creator name', code.creatorName, (value) =>
                this.setCodeField(index, 'creatorName', value)
              )}
              {this.renderTextControl(`code.${index}.creatorSlug`, 'Creator slug', code.creatorSlug, (value) =>
                this.setCodeField(index, 'creatorSlug', value)
              )}
              {this.renderTextControl(`code.${index}.creatorEmail`, 'Creator email', code.creatorEmail, (value) =>
                this.setCodeField(index, 'creatorEmail', value)
              )}
              {this.renderTextControl(`code.${index}.commissionPercent`, 'Commissione %', code.commissionPercent, (value) =>
                this.setCodeNumberField(index, 'commissionPercent', value), 'number'
              )}
            </div>
            <BootstrapForm.Group className={css(s.field)}>
              <BootstrapForm.Check
                type="checkbox"
                label="Enabled"
                checked={code.enabled ?? false}
                onChange={(event: any) => this.setCodeField(index, 'enabled', event.currentTarget.checked)}
              />
            </BootstrapForm.Group>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderCodesSection = () => {
    const hasErrors = this.hasErrorPrefix('code.')

    return (
      <Card className={css(hasErrors && s.cardWithErrors)}>
        <Card.Header className={css(s.cardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey="codes" className={css(s.accordionToggle)}>
            {this.renderAccordionTitle(
              <>
                Codici {hasErrors && <span className={css(s.errorBadge)}>Errori</span>}
              </>,
              this.state.activeSectionKey === 'codes'
            )}
          </Accordion.Toggle>
          <Button variant="outline-primary" onClick={this.addCode}>
            Aggiungi codice
          </Button>
        </Card.Header>
        <Accordion.Collapse eventKey="codes">
          <Card.Body>
            <Accordion
              activeKey={this.state.activeCodeKey}
              onSelect={(eventKey: any) => this.setState({ activeCodeKey: eventKey ?? '' })}
            >
              {this.state.campaign.codes.map(this.renderCode)}
            </Accordion>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderJsonPreview = (value: string) => {
    try {
      return (
        <div className={css(s.jsonPreview)}>
          <ReactJson
            collapsed={1}
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={false}
            name={false}
            src={this.parseJson(value, 'Metadata')}
          />
        </div>
      )
    } catch (e) {
      return <Alert variant="warning">Metadata JSON non valido. Apri la modale per correggerlo.</Alert>
    }
  }

  renderMetadataSection = () => {
    return (
      <Card>
        <Card.Header className={css(s.cardHeader)}>
          <Accordion.Toggle as={Button} variant="link" eventKey="metadata" className={css(s.accordionToggle)}>
            {this.renderAccordionTitle('Metadata sensibili', this.state.activeSectionKey === 'metadata')}
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey="metadata">
          <Card.Body>
            <div className={css(s.metadataBlock)}>
              <div className={css(s.sectionHeader)}>
                <h5>Metadata campagna</h5>
                <Button variant="outline-warning" size="sm" onClick={() => this.openMetadataModal('campaign')}>
                  Modifica metadata
                </Button>
              </div>
              {this.renderJsonPreview(this.state.campaignMetadataJson)}
            </div>

            {this.state.campaign.codes.map((code, index) => (
              <div className={css(s.metadataBlock)} key={`code-metadata-${index}`}>
                <div className={css(s.sectionHeader)}>
                  <h5>
                    Metadata codice {index + 1}
                    {code.code ? ` - ${code.code}` : ''}
                  </h5>
                  <Button variant="outline-warning" size="sm" onClick={() => this.openMetadataModal('code', index)}>
                    Modifica metadata
                  </Button>
                </div>
                {this.renderJsonPreview(this.state.codeMetadataJsons[index] ?? '{}')}
              </div>
            ))}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    )
  }

  renderMetadataModal = () => {
    const { jsonModal } = this.state
    const title = jsonModal.target === 'campaign' ? 'Metadata campagna' : `Metadata codice ${(jsonModal.codeIndex ?? 0) + 1}`

    return (
      <Modal show={jsonModal.visible} onHide={this.closeMetadataModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        {!jsonModal.warningAccepted ? (
          <>
            <Modal.Body>
              <Alert variant="warning">
                Stai modificando un campo sensibile. Valori JSON errati possono cambiare il comportamento della campagna
                o rendere piu difficile il debug dei referral.
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeMetadataModal}>
                Annulla
              </Button>
              <Button variant="warning" onClick={this.acceptMetadataWarning}>
                Continua
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Body>
              {jsonModal.error && <Alert variant="danger">{jsonModal.error}</Alert>}
              <BootstrapForm.Control
                as="textarea"
                rows={16}
                value={jsonModal.draft}
                onChange={(event: any) =>
                  this.setState({
                    jsonModal: { ...this.state.jsonModal, draft: event.currentTarget.value, error: undefined },
                  })
                }
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeMetadataModal}>
                Annulla
              </Button>
              <Button variant="primary" onClick={this.saveMetadataModal}>
                Salva metadata
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    )
  }

  render() {
    const { loading, saving } = this.state

    if (!isSuperAdmin())
      return <h1 style={{ textAlign: 'center', marginTop: 50 }}>Non puoi accedere a questa pagina</h1>

    return (
      <Page ref={(r) => (this.page = r!)} page={pages.CREATE_REFERRAL_CAMPAIGN}>
        {loading && <Spinner animation="border" />}
        {!loading && (
          <>
            <Header title={this.props.campaignKey ? 'Modifica campagna referral' : 'Crea campagna referral'} />
            {this.renderMetadataModal()}
            <BootstrapForm onSubmit={this.onSubmit}>
              {this.renderErrorSummary()}
              <Accordion
                activeKey={this.state.activeSectionKey}
                onSelect={(eventKey: any) => this.setState({ activeSectionKey: eventKey ?? '' })}
              >
                {this.renderCampaignSection()}
                {this.renderProductsSection()}
                {this.renderCodesSection()}
                {this.renderMetadataSection()}
              </Accordion>
              <Button variant="primary" type="submit" disabled={saving} className={css(s.submitButton)}>
                {saving ? 'Salvataggio...' : 'Conferma'}
              </Button>
            </BootstrapForm>
          </>
        )}
      </Page>
    )
  }
}

const s = StyleSheet.create({
  accordionArrow: {
    color: 'var(--color-muted)',
    display: 'inline-block',
    flexShrink: 0,
    fontSize: 13,
    lineHeight: 1,
    transform: 'rotate(0deg)',
    transformOrigin: 'center',
    transition: 'transform var(--dur-short) var(--ease-out), color var(--dur-short) var(--ease-out)',
    width: 10,
  },
  accordionArrowOpen: {
    color: 'var(--color-ink)',
    transform: 'rotate(90deg)',
  },
  accordionTitle: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 8,
    minWidth: 0,
  },
  accordionTitleText: {
    minWidth: 0,
  },
  accordionToggle: {
    color: 'var(--color-ink)',
    fontWeight: 600,
    padding: 0,
    textAlign: 'left',
    textDecoration: 'none',
    whiteSpace: 'normal',
    ':hover': {
      textDecoration: 'none',
    },
  },
  cardHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  cardWithErrors: {
    borderColor: 'var(--color-error)',
  },
  errorBadge: {
    backgroundColor: 'var(--color-error)',
    borderRadius: 3,
    color: 'var(--color-accent-ink)',
    fontSize: 11,
    marginLeft: 8,
    padding: '2px 6px',
  },
  errorLink: {
    color: 'var(--color-error)',
    padding: 0,
    textAlign: 'left',
  },
  errorList: {
    marginBottom: 0,
    marginTop: 8,
    paddingLeft: 18,
  },
  errorSummary: {
    marginBottom: 18,
  },
  field: {
    marginTop: 10,
  },
  grid: {
    display: 'grid',
    gridColumnGap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  headerActions: {
    display: 'flex',
    flexShrink: 0,
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-end',
  },
  innerCard: {
    marginBottom: 12,
  },
  innerCardHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
  },
  jsonPreview: {
    backgroundColor: 'var(--color-paper-3)',
    border: '1px solid var(--color-rule)',
    borderRadius: 4,
    padding: 12,
  },
  metadataBlock: {
    borderBottom: '1px solid var(--color-rule)',
    marginBottom: 16,
    paddingBottom: 16,
  },
  previewGrid: {
    backgroundColor: 'var(--color-paper-3)',
    border: '1px solid var(--color-rule)',
    borderRadius: 4,
    display: 'grid',
    gridGap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginTop: 12,
    padding: 12,
  },
  previewItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  previewValue: {
    color: 'var(--color-ink-2)',
    wordBreak: 'break-word',
  },
  previewMuted: {
    backgroundColor: 'var(--color-paper-3)',
    border: '1px dashed var(--color-rule-2)',
    borderRadius: 4,
    color: 'var(--color-muted)',
    marginTop: 12,
    padding: 12,
  },
  requiredMark: {
    color: 'var(--color-error)',
    fontWeight: 700,
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  submitButton: {
    marginBottom: 48,
    marginTop: 24,
    minWidth: 180,
  },
  summaryMuted: {
    color: 'var(--color-muted)',
    fontWeight: 400,
    marginLeft: 6,
  },
})
